import type {
  ApiErrorBody,
  ApiSuccessBody,
  Application,
  AuditEntry,
  Channel,
  ChannelAssignment,
  CreateApplicationInput,
  CreateReleaseInput,
  OverviewMetrics,
  PaginationMeta,
  ProductionChannelSummary,
  Release,
  SettingsInfo,
  UpdateApplicationInput,
} from '@ckp-ota/shared';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');

/** Optional bearer for AUTH_MODE=development (or pasted JWT). Not used for password cookies. */
const TOKEN_KEY = 'ckp_ota_auth_token';
/** @deprecated legacy key from earlier local-dev flow */
const LEGACY_TOKEN_KEY = 'ckp_ota_dev_token';
/** UX marker only — real auth is HttpOnly cookies. Cleared on logout / failed refresh. */
const SESSION_FLAG_KEY = 'ckp_ota_session';
const CSRF_KEY = 'ckp_ota_csrf';

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public requestId?: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

let memoryCsrf: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

export function getAuthToken(): string | null {
  return (
    localStorage.getItem(TOKEN_KEY) ||
    localStorage.getItem(LEGACY_TOKEN_KEY)
  );
}

export function setAuthToken(token: string | null): void {
  if (!token) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    return;
  }
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  // Bearer paste path — mark session for router guards.
  localStorage.setItem(SESSION_FLAG_KEY, '1');
}

/** @deprecated use getAuthToken */
export const getDevToken = getAuthToken;
/** @deprecated use setAuthToken */
export const setDevToken = setAuthToken;

export function getCsrfToken(): string | null {
  return memoryCsrf || sessionStorage.getItem(CSRF_KEY);
}

export function setCsrfToken(token: string | null): void {
  memoryCsrf = token;
  if (!token) {
    sessionStorage.removeItem(CSRF_KEY);
    return;
  }
  sessionStorage.setItem(CSRF_KEY, token);
}

export function markSession(active: boolean): void {
  if (active) {
    localStorage.setItem(SESSION_FLAG_KEY, '1');
  } else {
    localStorage.removeItem(SESSION_FLAG_KEY);
  }
}

export function isLoggedIn(): boolean {
  return (
    localStorage.getItem(SESSION_FLAG_KEY) === '1' || Boolean(getAuthToken())
  );
}

function clearClientAuthState(): void {
  setAuthToken(null);
  setCsrfToken(null);
  markSession(false);
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    // Clear local state even if the network call fails.
  }
  clearClientAuthState();
}

function parseSetCsrf(payload: unknown): void {
  if (
    payload &&
    typeof payload === 'object' &&
    'data' in payload &&
    payload.data &&
    typeof payload.data === 'object' &&
    'csrf_token' in payload.data &&
    typeof (payload.data as { csrf_token: unknown }).csrf_token === 'string'
  ) {
    setCsrfToken((payload.data as { csrf_token: string }).csrf_token);
  }
}

async function performRefreshRequest(): Promise<boolean> {
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  const payload = (await res.json().catch(() => null)) as
    | ApiSuccessBody<{ csrf_token: string; authenticated: boolean }>
    | ApiErrorBody
    | null;

  if (res.ok && payload && 'data' in payload) {
    parseSetCsrf(payload);
    markSession(true);
    return true;
  }

  const message =
    payload && 'error' in payload ? payload.error.message : '';
  // Concurrent rotation race: winner already updated the shared cookie jar.
  // Do not clear auth — retry once so this tab picks up the new refresh cookie.
  if (res.status === 401 && message.includes('already rotated')) {
    await new Promise((r) => setTimeout(r, 50));
    const retry = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    const retryPayload = (await retry.json().catch(() => null)) as
      | ApiSuccessBody<{ csrf_token: string; authenticated: boolean }>
      | ApiErrorBody
      | null;
    if (retry.ok && retryPayload && 'data' in retryPayload) {
      parseSetCsrf(retryPayload);
      markSession(true);
      return true;
    }
  }

  clearClientAuthState();
  return false;
}

/**
 * Silently renew the HttpOnly session.
 * Same-tab callers share one in-flight promise; cross-tab callers use Web Locks
 * so concurrent refreshes do not trigger false-positive reuse revocation.
 */
export async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  const run = async (): Promise<boolean> => {
    try {
      return await performRefreshRequest();
    } catch {
      clearClientAuthState();
      return false;
    }
  };

  const locks = (
    globalThis as unknown as {
      navigator?: { locks?: { request: (name: string, cb: () => Promise<boolean>) => Promise<boolean> } };
    }
  ).navigator?.locks;

  refreshInFlight = (async () => {
    try {
      if (locks?.request) {
        return await locks.request('ckp-ota-auth-refresh', run);
      }
      return await run();
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/** Restore CSRF after browser restart when access cookie may still be valid. */
export async function ensureSession(): Promise<boolean> {
  if (!isLoggedIn() && !getAuthToken()) {
    // May still have cookies from a prior visit without a marker (upgrade path).
  }

  if (getAuthToken()) {
    markSession(true);
    return true;
  }

  try {
    const res = await fetch(`${API_BASE}/auth/session`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    const payload = (await res.json().catch(() => null)) as
      | ApiSuccessBody<{ csrf_token: string; authenticated: boolean }>
      | ApiErrorBody
      | null;
    if (res.ok && payload && 'data' in payload) {
      parseSetCsrf(payload);
      markSession(true);
      return true;
    }
  } catch {
    // Fall through to refresh.
  }

  return refreshSession();
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  allowRetry = true,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const token = getAuthToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const method = (init.method || 'GET').toUpperCase();
  const csrf = getCsrfToken();
  if (csrf && method !== 'GET' && method !== 'HEAD') {
    headers.set('X-CSRF-Token', csrf);
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });
  const payload = (await res.json().catch(() => null)) as
    | ApiSuccessBody<T>
    | ApiErrorBody
    | null;

  if (res.status === 401 && allowRetry && !token && !path.startsWith('/auth/')) {
    const renewed = await refreshSession();
    if (renewed) {
      return request<T>(path, init, false);
    }
    // Refresh failed — session is actually gone.
    clearClientAuthState();
  }

  if (!res.ok || (payload && 'error' in payload)) {
    const err = payload && 'error' in payload ? payload.error : null;
    // Do not clear the session marker on every 401. Push/proxy or CSRF errors can
    // return 401 while cookies are still valid; clearing here bounced users to login
    // on the next navigation. Session is cleared only when refresh fails above.
    throw new ApiClientError(
      res.status,
      err?.code ?? 'internal_error',
      err?.message ?? `Request failed (${res.status})`,
      payload && 'request_id' in payload ? payload.request_id : undefined,
      err?.details,
    );
  }

  return (payload as ApiSuccessBody<T>).data;
}

export const api = {
  login: async (password: string) => {
    const data = await request<{
      authenticated: boolean;
      expires_in: number;
      csrf_token: string;
    }>('/auth/login', { method: 'POST', body: JSON.stringify({ password }) }, false);
    setCsrfToken(data.csrf_token);
    markSession(true);
    // Prefer cookie session over any leftover local bearer.
    setAuthToken(null);
    markSession(true);
    return data;
  },

  logout: () => logout(),

  refreshSession: () => refreshSession(),

  ensureSession: () => ensureSession(),

  getOverview: () =>
    request<{
      metrics: OverviewMetrics;
      recent_activity: AuditEntry[];
      production_channels: ProductionChannelSummary[];
    }>('/admin/overview'),

  listApplications: (page = 1, pageSize = 50) =>
    request<{ items: Application[]; pagination: PaginationMeta }>(
      `/admin/applications?page=${page}&page_size=${pageSize}`,
    ),

  getApplication: (id: string) =>
    request<{
      application: Application;
      channels: (Channel & { current_release: Release | null })[];
    }>(`/admin/applications/${id}`),

  createApplication: (input: CreateApplicationInput) =>
    request<{ application: Application; channels: Channel[] }>(
      '/admin/applications',
      { method: 'POST', body: JSON.stringify(input) },
    ),

  updateApplication: (id: string, input: UpdateApplicationInput) =>
    request<{ application: Application }>(`/admin/applications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  listReleases: (page = 1, pageSize = 50, applicationId?: string) => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    if (applicationId) params.set('application_id', applicationId);
    return request<{ items: Release[]; pagination: PaginationMeta }>(
      `/admin/releases?${params}`,
    );
  },

  getRelease: (id: string) =>
    request<{ release: Release }>(`/admin/releases/${id}`),

  createRelease: (input: CreateReleaseInput) =>
    request<{ release: Release }>('/admin/releases', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  uploadBundle: async (
    releaseId: string,
    file: File,
    onProgress?: (pct: number) => void,
  ) => {
    const token = getAuthToken();
    const csrf = getCsrfToken();
    return new Promise<{ release: Release }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', `${API_BASE}/admin/releases/${releaseId}/bundle`);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader('Content-Type', 'application/zip');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      if (csrf) xhr.setRequestHeader('X-CSRF-Token', csrf);
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable && onProgress) {
          onProgress(Math.round((ev.loaded / ev.total) * 100));
        }
      };
      xhr.onload = async () => {
        try {
          if (xhr.status === 401 && !token) {
            const renewed = await refreshSession();
            if (renewed) {
              try {
                const retry = await api.uploadBundle(releaseId, file, onProgress);
                resolve(retry);
              } catch (e) {
                reject(e);
              }
              return;
            }
            clearClientAuthState();
          }
          const payload = JSON.parse(xhr.responseText) as
            | ApiSuccessBody<{ release: Release }>
            | ApiErrorBody;
          if (xhr.status >= 200 && xhr.status < 300 && 'data' in payload) {
            resolve(payload.data);
          } else {
            const err = 'error' in payload ? payload.error : null;
            reject(
              new ApiClientError(
                xhr.status,
                err?.code ?? 'internal_error',
                err?.message ?? 'Upload failed',
              ),
            );
          }
        } catch (e) {
          reject(e);
        }
      };
      xhr.onerror = () => reject(new ApiClientError(0, 'internal_error', 'Network error'));
      xhr.send(file);
    });
  },

  publishRelease: (id: string) =>
    request<{ release: Release }>(`/admin/releases/${id}/publish`, {
      method: 'POST',
      body: '{}',
    }),

  revokeRelease: (id: string) =>
    request<{ release: Release }>(`/admin/releases/${id}/revoke`, {
      method: 'POST',
      body: '{}',
    }),

  getChannel: (id: string) =>
    request<{ channel: Channel; current_release: Release | null }>(
      `/admin/channels/${id}`,
    ),

  updateChannel: (
    id: string,
    input: { enabled?: boolean; rotate_access_token?: boolean },
  ) =>
    request<{ channel: Channel; access_token?: string }>(`/admin/channels/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  assignChannel: (
    id: string,
    input: {
      release_id: string;
      action?: 'assign' | 'promote' | 'rollback';
      notes?: string | null;
      confirm_production?: boolean;
    },
  ) =>
    request<{ channel: Channel; release: Release }>(`/admin/channels/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  channelHistory: (id: string, page = 1, pageSize = 20) =>
    request<{ items: ChannelAssignment[]; pagination: PaginationMeta }>(
      `/admin/channels/${id}/history?page=${page}&page_size=${pageSize}`,
    ),

  listAudit: (page = 1, pageSize = 50, applicationId?: string) => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    if (applicationId) params.set('application_id', applicationId);
    return request<{ items: AuditEntry[]; pagination: PaginationMeta }>(
      `/admin/audit?${params}`,
    );
  },

  getSettings: () => request<SettingsInfo>('/admin/settings'),

  getReadiness: () =>
    request<{
      status: string;
      checks: Record<string, { ok: boolean; detail: string }>;
      environment: string;
      auth_mode: string;
      api_version: string;
    }>('/admin/readiness'),

  listPublisherTokens: () =>
    request<{
      items: {
        id: string;
        name: string;
        token_prefix: string;
        application_id: string | null;
        scopes: string[];
        created_at: string;
        last_used_at: string | null;
        revoked_at: string | null;
      }[];
    }>('/admin/publisher-tokens'),

  createPublisherToken: (input: {
    name: string;
    application_id?: string | null;
    scopes?: string[];
  }) =>
    request<{
      token: {
        id: string;
        name: string;
        token: string;
        application_id: string | null;
        scopes: string[];
        created_at: string;
      };
    }>('/admin/publisher-tokens', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  revokePublisherToken: (id: string) =>
    request<{ revoked: boolean }>(`/admin/publisher-tokens/${id}/revoke`, {
      method: 'POST',
      body: '{}',
    }),

  // ---- Push (proxied to ckp-ota-push Worker) ----
  upsertPushApplication: (
    applicationId: string,
    input: {
      name: string;
      android_package_id?: string | null;
      ios_bundle_id?: string | null;
      categories?: string[];
      status?: 'active' | 'disabled';
    },
  ) =>
    request<{ application: PushApplication }>(
      `/admin/push/applications/${applicationId}`,
      { method: 'PUT', body: JSON.stringify(input) },
    ),

  getPushApplication: (applicationId: string, environment = 'production') =>
    request<{
      application: PushApplication;
      credentials: PushCredentialStatus;
      eligible: { total: number; android: number; ios: number };
      byCategory: Record<string, number>;
    }>(`/admin/push/applications/${applicationId}?environment=${environment}`),

  listPushDevices: (applicationId: string, environment?: string) => {
    const q = environment ? `?environment=${environment}` : '';
    return request<{ items: PushDevice[] }>(
      `/admin/push/applications/${applicationId}/devices${q}`,
    );
  },

  putPushCredentials: (
    applicationId: string,
    environment: 'development' | 'production',
    input: Record<string, string | null | undefined>,
  ) =>
    request<{ credentials: PushCredentialStatus }>(
      `/admin/push/applications/${applicationId}/credentials/${environment}`,
      { method: 'PUT', body: JSON.stringify(input) },
    ),

  previewPushAnnounce: (
    applicationId: string,
    input: {
      environment: 'development' | 'production';
      audience: 'all_opted_in' | 'test_devices' | 'category';
      category?: string | null;
      title?: string;
      body?: string;
    },
  ) =>
    request<{
      eligible: { total: number; android: number; ios: number };
      preview: { title: string; body: string };
    }>(`/admin/push/applications/${applicationId}/announce/preview`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  sendPushAnnounce: (
    applicationId: string,
    input: {
      announcementId: string;
      environment: 'development' | 'production';
      title: string;
      body: string;
      data?: Record<string, string>;
      category?: string | null;
      audience: 'all_opted_in' | 'test_devices' | 'category';
      confirm: boolean;
      release_verification?: {
        method: 'manual_ack' | 'external_catalog';
        acknowledged?: boolean;
        note?: string;
        series_id?: string;
      } | null;
    },
  ) =>
    request<{
      alreadyCompleted: boolean;
      announcement: PushAnnouncement;
    }>(`/admin/push/applications/${applicationId}/announce`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  listPushAnnouncements: (applicationId: string) =>
    request<{ items: PushAnnouncement[] }>(
      `/admin/push/applications/${applicationId}/announcements`,
    ),
};

export interface PushApplication {
  application_id: string;
  name: string;
  android_package_id: string | null;
  ios_bundle_id: string | null;
  categories: string[];
  status: 'active' | 'disabled';
}

export interface PushCredentialStatus {
  application_id: string;
  environment: string;
  fcm_configured: boolean;
  apns_configured: boolean;
  updated_at: string | null;
}

export interface PushDevice {
  installation_id: string;
  application_id: string;
  environment: string;
  platform: string;
  token_valid: boolean;
  categories: Record<string, boolean>;
  is_test_device: boolean;
  has_token?: boolean;
  created_at: string;
  updated_at: string;
}

export interface PushAnnouncement {
  announcement_id: string;
  application_id: string;
  environment: string;
  category: string | null;
  title: string;
  body: string;
  data: Record<string, string>;
  audience: string;
  status: string;
  eligible_count: number;
  sent_count: number;
  failed_count: number;
  skipped_count: number;
  created_at: string;
  completed_at: string | null;
}
